/**
 * Scroll synchronisation by content anchor.
 *
 * The controller never sends a pixel offset. It sends the id of a segment plus
 * how far through that segment it is looking, and each viewer resolves that
 * against its own layout. That is what makes a phone and a laptop point at the
 * same sentence, and what stops new text arriving underneath from shifting
 * where everyone is looking.
 *
 * Pure geometry, no DOM: the measurements come in as plain numbers so the
 * round-trip can be tested across viewport sizes.
 */
import type { ScrollAnchor } from '@lt/protocol';

/**
 * Where in the viewport the anchor sits — a third from the top rather than the
 * very top, so the line being discussed has context above it and the same
 * relative position on every screen.
 */
export const ANCHOR_VIEWPORT_FRACTION = 1 / 3;

/** One measured segment element, in content coordinates. */
export interface SegmentBox {
  id: string;
  /** Distance from the top of the scrollable content. */
  top: number;
  height: number;
  /** Distance from the left of the scroll container. */
  left?: number;
  width?: number;
}

export interface Viewport {
  scrollTop: number;
  /** Visible height of the scroll container. */
  clientHeight: number;
  /** Total height of the content. */
  scrollHeight: number;
}

const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));

/**
 * The content position the viewer is "looking at": the anchor line, expressed
 * in content coordinates.
 */
export function anchorContentY(viewport: Viewport): number {
  return viewport.scrollTop + viewport.clientHeight * ANCHOR_VIEWPORT_FRACTION;
}

/**
 * Derives the anchor to broadcast from what the controller currently sees.
 *
 * @returns null when there is nothing to anchor to — an empty transcript.
 */
export function anchorFromViewport(
  boxes: readonly SegmentBox[],
  viewport: Viewport,
  now = Date.now(),
): ScrollAnchor | null {
  if (boxes.length === 0) return null;
  const target = anchorContentY(viewport);

  // Above the first line or below the last: clamp to the end rather than
  // reporting nothing, so a controller at the very top or bottom still syncs.
  const first = boxes[0]!;
  if (target <= first.top) return { segmentId: first.id, fraction: 0, sentAt: now };
  const last = boxes[boxes.length - 1]!;
  if (target >= last.top + last.height) return { segmentId: last.id, fraction: 1, sentAt: now };

  for (const box of boxes) {
    if (target >= box.top && target < box.top + box.height) {
      const fraction = box.height > 0 ? (target - box.top) / box.height : 0;
      return { segmentId: box.id, fraction: clamp(fraction, 0, 1), sentAt: now };
    }
  }

  // Between two boxes (a gap or margin): anchor to the one that just ended.
  let best = first;
  for (const box of boxes) if (box.top <= target) best = box;
  return { segmentId: best.id, fraction: 1, sentAt: now };
}

/**
 * The scrollTop that puts `anchor` at the viewport's anchor line.
 *
 * @returns null when the anchored segment has not arrived yet — the caller
 *   holds the anchor as pending and applies it the moment the segment does.
 */
export function scrollTopForAnchor(
  anchor: Pick<ScrollAnchor, 'segmentId' | 'fraction'>,
  boxes: readonly SegmentBox[],
  viewport: Pick<Viewport, 'clientHeight' | 'scrollHeight'>,
): number | null {
  const box = boxes.find((b) => b.id === anchor.segmentId);
  if (!box) return null;
  const contentY = box.top + box.height * clamp(anchor.fraction, 0, 1);
  const desired = contentY - viewport.clientHeight * ANCHOR_VIEWPORT_FRACTION;
  // Clamping is why two viewers can disagree slightly near the ends of a short
  // transcript: neither can scroll past its own content.
  return clamp(desired, 0, Math.max(0, viewport.scrollHeight - viewport.clientHeight));
}

/**
 * True when the viewer is already close enough that re-scrolling would only
 * cause jitter. Follow mode runs every frame, so this is what keeps it still.
 */
export function isCloseEnough(currentScrollTop: number, desiredScrollTop: number, tolerancePx = 2): boolean {
  return Math.abs(currentScrollTop - desiredScrollTop) <= tolerancePx;
}

/**
 * Tracks whether a viewer is following the controller.
 *
 * Following is on by default; a scroll the user initiated turns it off, and a
 * control turns it back on. The hard part is telling a user's scroll apart from
 * the one follow mode just performed, which is what `expectScrollTop` is for.
 */
export class FollowState {
  #following = true;
  #pending: Pick<ScrollAnchor, 'segmentId' | 'fraction'> | null = null;
  /** The scrollTop we last set ourselves, so its scroll event is not mistaken for the user's. */
  #expectScrollTop: number | null = null;

  get following(): boolean {
    return this.#following;
  }

  /** The anchor waiting for its segment to arrive, if any. */
  get pendingAnchor(): Pick<ScrollAnchor, 'segmentId' | 'fraction'> | null {
    return this.#pending;
  }

  resume(): void {
    this.#following = true;
  }

  /** Called when the user scrolls, with the scrollTop the event reports. */
  onScroll(scrollTop: number, tolerancePx = 4): boolean {
    if (this.#expectScrollTop !== null && Math.abs(scrollTop - this.#expectScrollTop) <= tolerancePx) {
      // Our own programmatic scroll landing — not the user taking over.
      this.#expectScrollTop = null;
      return this.#following;
    }
    this.#expectScrollTop = null;
    this.#following = false;
    return false;
  }

  /**
   * Applies an incoming anchor.
   *
   * @returns the scrollTop to apply, or null when nothing should move — either
   *   because the viewer has stopped following, or because the anchored segment
   *   has not arrived and the anchor is now pending.
   */
  receive(
    anchor: Pick<ScrollAnchor, 'segmentId' | 'fraction'>,
    boxes: readonly SegmentBox[],
    viewport: Viewport,
  ): number | null {
    if (!this.#following) return null;
    const desired = scrollTopForAnchor(anchor, boxes, viewport);
    if (desired === null) {
      // The anchored segment is still in flight. Hold it: a growing transcript
      // must not make the viewer guess.
      this.#pending = anchor;
      return null;
    }
    this.#pending = null;
    if (isCloseEnough(viewport.scrollTop, desired)) return null;
    this.#expectScrollTop = desired;
    return desired;
  }

  /** Retries the pending anchor after new segments have been laid out. */
  retryPending(boxes: readonly SegmentBox[], viewport: Viewport): number | null {
    if (!this.#pending || !this.#following) return null;
    return this.receive(this.#pending, boxes, viewport);
  }
}
